import { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "@/components/ui/sonner";
import { AlertTriangle, Shield, ShieldCheck, Smartphone, Plus, X } from "lucide-react";

function parseClaims(){
  try { const t=localStorage.getItem('token')||''; const p = JSON.parse(atob((t.split('.')[1]||''))); return p||{}; } catch { return {}; }
}

export default function Profile(){
  const [me,setMe]=useState(null);
  const [org,setOrg]=useState(null);
  const [form,setForm]=useState({ password:"", password2:"" });
  const [hasTotp, setHasTotp] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // Add Device state
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [addDeviceStep, setAddDeviceStep] = useState(1); // 1=enter code, 2=show QR
  const [verifyCode, setVerifyCode] = useState("");
  const [addDeviceQr, setAddDeviceQr] = useState("");
  const [addDeviceSecret, setAddDeviceSecret] = useState("");
  const [addingDevice, setAddingDevice] = useState(false);

  const [oaStatus,setOaStatus]=useState('loading');
  const [oaStatusHint,setOaStatusHint]=useState(null);

  const load=async()=>{
    try{
      const r=await api.get('/me');
      setMe(r.data);
      const claims = parseClaims();
      const orgId = r.data?.org_id || claims.org;
      if(orgId){
        try{ const o=await api.get(`/orgs/by-id/${orgId}`); setOrg(o.data); } catch {}
      }
      try{ const s=await api.get('/owner/settings'); setOaStatus(s?.data?.has_openai_key? 'stored':'missing'); }catch{ setOaStatus('error'); }
      
      // Check TOTP status
      try{ const t=await api.get('/me/totp-status'); setHasTotp(t.data?.has_totp); }catch{ setHasTotp(false); }

    }catch(e){ toast("Failed to load profile"); }
  };
  useEffect(()=>{ load(); },[]);

  const handleResetTotp = async () => {
    setResetting(true);
    try {
      await api.post('/me/reset-totp');
      toast.success("Authenticator reset successfully. You'll set up a new one on next login.");
      // Log the user out so they can set up a new authenticator
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 2000);
    } catch (e) {
      toast.error("Failed to reset authenticator");
      setResetting(false);
      setResetConfirm(false);
    }
  };

  const handleAddDevice = async () => {
    if (!verifyCode || verifyCode.length < 6) {
      toast.error("Please enter the 6-digit code from your current authenticator");
      return;
    }
    
    setAddingDevice(true);
    try {
      const r = await api.post('/me/totp-add-device', { code: verifyCode.trim() });
      setAddDeviceQr(r.data.qr_code);
      setAddDeviceSecret(r.data.secret);
      setAddDeviceStep(2);
      toast.success("Verified! Scan the QR code with your new device.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code - please try again");
      setVerifyCode("");
    } finally {
      setAddingDevice(false);
    }
  };

  const closeAddDevice = () => {
    setShowAddDevice(false);
    setAddDeviceStep(1);
    setVerifyCode("");
    setAddDeviceQr("");
    setAddDeviceSecret("");
  };

  const savePass=async()=>{
    if(form.password!==form.password2){ toast("Passwords do not match"); return; }
    try{ await api.post('/me/password', { password: form.password }); toast("Password updated"); setForm({ password:"", password2:"" }); } catch { toast("Failed to update password"); }
  };

  const saveOrg=async()=>{
    if(!org?.id) return;
    try{ const r=await api.patch(`/my-org`, { name: org.name, logo_url: org.logo_url }); setOrg(r.data); toast("Organisation updated"); } catch { toast("Failed to update org"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#171717]" style={{ fontFamily: "'Playfair Display', serif" }}>My Profile</h1>
        <p className="text-sm text-gray-500">Manage your account settings and security.</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Change Password</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input type="password" placeholder="New password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} />
          <Input type="password" placeholder="Confirm new password" value={form.password2} onChange={(e)=>setForm({...form,password2:e.target.value})} />
          <div className="col-span-full"><Button onClick={savePass}>Save Password</Button></div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication Card */}
      <Card data-testid="totp-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {hasTotp ? <ShieldCheck className="w-5 h-5 text-green-600" /> : <Shield className="w-5 h-5 text-amber-500" />}
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Manage your authenticator app for secure login
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-sm">Authenticator App</div>
              <div className="text-xs text-muted-foreground">
                {hasTotp === null ? "Loading..." : 
                 hasTotp ? "✓ Enabled - Your account is protected" : 
                 "⚠ Not set up - You'll be prompted on next login"}
              </div>
            </div>
            {hasTotp && (
              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">Active</span>
            )}
          </div>

          {hasTotp && (
            <div className="border-t pt-4 space-y-4">
              {/* Add Another Device Section */}
              {!showAddDevice && !resetConfirm && (
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddDevice(true)}
                    data-testid="add-device-btn"
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Device
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setResetConfirm(true)}
                    data-testid="reset-totp-btn"
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Reset Authenticator
                  </Button>
                </div>
              )}

              {/* Add Another Device Flow */}
              {showAddDevice && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-800">Add Another Device</span>
                    </div>
                    <button onClick={closeAddDevice} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {addDeviceStep === 1 && (
                    <div className="space-y-3">
                      <p className="text-sm text-blue-700">
                        Enter the code from your current authenticator to verify your identity:
                      </p>
                      <Input 
                        placeholder="000000" 
                        value={verifyCode} 
                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="text-center text-xl tracking-widest bg-white"
                        maxLength={6}
                        data-testid="add-device-verify-code"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddDevice()}
                      />
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleAddDevice}
                          disabled={verifyCode.length < 6 || addingDevice}
                          size="sm"
                          data-testid="add-device-verify-btn"
                        >
                          {addingDevice ? "Verifying..." : "Verify & Show QR Code"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={closeAddDevice}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {addDeviceStep === 2 && (
                    <div className="space-y-3">
                      <p className="text-sm text-blue-700">
                        Scan this QR code with your new authenticator app:
                      </p>
                      <div className="flex justify-center">
                        {addDeviceQr && (
                          <img 
                            src={addDeviceQr} 
                            alt="TOTP QR Code" 
                            className="border rounded-lg p-2 bg-white"
                            style={{ maxWidth: "180px" }}
                          />
                        )}
                      </div>
                      {addDeviceSecret && (
                        <div className="text-center bg-white p-2 rounded border">
                          <p className="text-xs text-gray-500 mb-1">Or enter manually:</p>
                          <code className="text-xs font-mono select-all">{addDeviceSecret}</code>
                        </div>
                      )}
                      <div className="bg-green-100 p-2 rounded text-xs text-green-800">
                        ✓ Both devices will generate the same codes. You can use either one to log in.
                      </div>
                      <Button variant="outline" size="sm" onClick={closeAddDevice} className="w-full">
                        Done
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Reset Authenticator Flow */}
              {resetConfirm && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="space-y-3">
                      <div>
                        <div className="font-medium text-amber-800">Reset your authenticator?</div>
                        <p className="text-sm text-amber-700 mt-1">
                          This will remove your current authenticator from all devices. You'll be logged out and asked to set up a new one on your next login.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={handleResetTotp}
                          disabled={resetting}
                          data-testid="confirm-reset-totp"
                        >
                          {resetting ? "Resetting..." : "Yes, Reset & Log Out"}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setResetConfirm(false)}
                          disabled={resetting}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!hasTotp && hasTotp !== null && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                You haven't set up an authenticator app yet. Log out and log back in to complete the setup process.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {org && (
        <Card>
          <CardHeader><CardTitle>My Organisation</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input value={org.name||""} onChange={(e)=>setOrg({...org,name:e.target.value})} />
            <Input value={org.logo_url||""} onChange={(e)=>setOrg({...org,logo_url:e.target.value})} placeholder="Logo URL or upload below" />
            <div className="col-span-full">
              <label className="block text-sm mb-1">Upload Logo (PNG/JPG/WebP)</label>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={async(e)=>{
                const f=e.target.files?.[0]; if(!f||!org?.id) return;
                try{
                  const form=new FormData(); form.append('file', f);
                  const r=await api.post(`/my-org/logo`, form, { headers:{ 'Content-Type':'multipart/form-data' }});
                  setOrg({...org, logo_url:r.data.logo_url}); toast("Logo uploaded");
                }catch(err){ toast("Upload failed"); }
              }} />
            </div>
            <div className="col-span-full"><Button onClick={saveOrg}>Save Organisation</Button></div>
          </CardContent>
        </Card>
      )}

      {me?.is_owner && (
        <Card>
          <CardHeader><CardTitle>Platform Settings</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm">OpenAI Key status: <span data-testid="oa-status">{oaStatus}</span> {oaStatus==='stored' && (<span data-testid="oa-hint" className="text-xs text-muted-foreground">(saved: {me?.is_owner? (oaStatusHint||'••••') : '••••'})</span>)} </div>
            <div className="flex items-center gap-2">
              <Input data-testid="oa-key" placeholder="sk-..." onChange={(e)=>window.__oa=e.target.value} />
              <Button data-testid="oa-save" onClick={async()=>{ try{ await api.post('/owner/settings', { openai_api_key: window.__oa||'' }); const v=await api.post('/owner/settings/validate'); if(v?.data?.ok){ setOaStatus('stored'); setOaStatusHint('••••'+(window.__oa||'').slice(-4)); toast('Saved & Validated'); } else { toast('Saved but validation failed'); } }catch(e){ toast(String(e?.response?.data?.detail||'Failed')); } }}>Save</Button>
            </div>
            <div className="text-xs text-muted-foreground">Use this to store/update the ChatGPT key used for email generation.</div>
          </CardContent>
        </Card>
      )}

      <Toaster position="top-right" richColors />
    </div>
  );
}
